import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  FileJson,
  ListTree,
  Loader2,
  RefreshCw,
  RotateCcw,
  Wrench,
  XCircle,
} from "lucide-react";

import { DashboardLayout } from "@/components/layout";
import {
  Button,
  Card,
  CardContent,
  ConfirmationModal,
  DataTable,
  DetailsSidebar,
  Input,
  PageHeader,
  StatusBadge,
} from "@/components/ui";
import {
  type ApprovalPipelineJobAction,
  type ApprovalPipelineJobDetailLedgerEntry,
  type ApprovalPipelineJobRecord,
  useApprovalPipelineJobs,
} from "@/hooks/useApprovalPipelineJobs";

function mapJobStatusToBadge(status: string): string {
  const normalized = status.trim().toUpperCase();
  if (normalized === "SUCCESS") return "ACTIVE";
  if (normalized === "FAILED" || normalized === "CANCELLED") return "FAILED";
  if (normalized === "RUNNING" || normalized === "DISPATCHING")
    return "PENDING";
  if (normalized === "QUEUED") return "WARNING";
  return "DISABLED";
}

function formatTimestamp(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function formatActionVariant(
  action: ApprovalPipelineJobAction,
): "outline" | "danger" {
  return action.destructive ? "danger" : "outline";
}

function renderLedgerPayload(payload: unknown): string | null {
  if (!payload) return null;
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return null;
  }
}

function JsonPanel({
  title,
  payload,
  emptyLabel,
}: {
  title: string;
  payload: unknown;
  emptyLabel: string;
}) {
  const formatted = renderLedgerPayload(payload);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
      <div className="flex items-center gap-2">
        <FileJson className="h-4 w-4 text-slate-500 dark:text-slate-400" />
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </p>
      </div>
      {formatted ? (
        <pre className="mt-3 max-h-72 overflow-auto rounded-md bg-slate-950 px-3 py-2 text-[11px] leading-5 text-slate-100">
          {formatted}
        </pre>
      ) : (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          {emptyLabel}
        </p>
      )}
    </div>
  );
}

function LedgerEntryCard({
  entry,
}: {
  entry: ApprovalPipelineJobDetailLedgerEntry;
}) {
  const payload = renderLedgerPayload(entry.payload);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={mapJobStatusToBadge(entry.status)}>
              {entry.status.toLowerCase()}
            </StatusBadge>
            {entry.eventType ? (
              <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {entry.eventType}
              </span>
            ) : null}
            {entry.retryable ? (
              <span className="rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                Retryable
              </span>
            ) : null}
          </div>
          {entry.error ? (
            <p className="text-sm text-rose-600 dark:text-rose-300">
              {entry.error}
            </p>
          ) : null}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {formatTimestamp(entry.createdAt)}
        </p>
      </div>

      {payload ? (
        <pre className="mt-3 overflow-x-auto rounded-md bg-slate-950 px-3 py-2 text-[11px] leading-5 text-slate-100">
          {payload}
        </pre>
      ) : null}
    </div>
  );
}

export default function ApprovalPipelineJobs() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const {
    jobs,
    loading,
    refreshing,
    errorMessage,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    filteredJobs,
    selectedSyncJobId,
    setSelectedSyncJobId,
    selectedJobDetail,
    selectedJobLoading,
    refresh,
    requestAction,
    requestResumeQueue,
    actionModal,
  } = useApprovalPipelineJobs();

  const summary = useMemo(
    () => ({
      active: jobs.filter((job) =>
        ["QUEUED", "DISPATCHING", "RUNNING"].includes(
          job.status.trim().toUpperCase(),
        ),
      ).length,
      failed: jobs.filter((job) =>
        ["FAILED", "CANCELLED"].includes(job.status.trim().toUpperCase()),
      ).length,
      retryable: jobs.filter((job) => Boolean(job.retryable)).length,
      total: jobs.length,
    }),
    [jobs],
  );

  const columns = useMemo(
    () => [
      {
        key: "status",
        label: "Status",
        sortable: false,
        width: "140px",
        render: (_value: unknown, row: Record<string, unknown>) => (
          <StatusBadge
            status={mapJobStatusToBadge(String(row.status ?? ""))}
            className="capitalize"
          >
            {String(row.status ?? "Unknown").toLowerCase()}
          </StatusBadge>
        ),
      },
      {
        key: "syncJobId",
        label: "Job",
        sortable: false,
        width: "220px",
        render: (value: unknown, row: Record<string, unknown>) => {
          const syncJobId = String(value ?? "—");
          return (
            <div>
              <p className="font-mono text-xs text-slate-900 dark:text-slate-100">
                {syncJobId}
              </p>
              <p className="mt-1 font-mono text-[11px] text-slate-500 dark:text-slate-400">
                Pipeline {String(row.pipelineId ?? "—")}
              </p>
            </div>
          );
        },
      },
      {
        key: "mappingSessionId",
        label: "Session Scope",
        sortable: false,
        render: (value: unknown, row: Record<string, unknown>) => (
          <div className="space-y-1">
            <p className="font-mono text-xs text-slate-900 dark:text-slate-100">
              {String(value ?? "—")}
            </p>
            <p className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
              {String(row.resourceLockKey ?? "No lock key")}
            </p>
          </div>
        ),
      },
      {
        key: "fieldDecisionCount",
        label: "Workload",
        sortable: false,
        width: "170px",
        render: (value: unknown, row: Record<string, unknown>) => (
          <div className="space-y-1 text-xs text-slate-600 dark:text-slate-300">
            <p>Fields: {String(value ?? 0)}</p>
            <p>
              Canonical values: {String(row.canonicalValueDecisionCount ?? 0)}
            </p>
            <p>
              Marketplace values:{" "}
              {String(row.marketplaceSpecificValueDecisionCount ?? 0)}
            </p>
          </div>
        ),
      },
      {
        key: "message",
        label: "Latest detail",
        sortable: false,
        render: (value: unknown, row: Record<string, unknown>) => (
          <div className="space-y-1">
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {String(value ?? row.error ?? "No details yet")}
            </p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Taxonomy: {String(row.taxonomyStatus ?? "—")}
            </p>
          </div>
        ),
      },
      {
        key: "occurredAt",
        label: "Last update",
        sortable: false,
        width: "180px",
        render: (value: unknown, row: Record<string, unknown>) => (
          <div className="space-y-1">
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {formatTimestamp(String(value ?? ""))}
            </p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Updated {formatTimestamp(String(row.updatedAt ?? ""))}
            </p>
          </div>
        ),
      },
      {
        key: "actions",
        label: "Recovery",
        sortable: false,
        width: "220px",
        render: (_value: unknown, row: Record<string, unknown>) => {
          const job = row as unknown as ApprovalPipelineJobRecord;
          if (job.actions.length === 0) {
            return (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                No direct action
              </p>
            );
          }

          return (
            <div className="flex flex-wrap gap-2">
              {job.actions.map((action) => (
                <Button
                  key={action.key}
                  variant={formatActionVariant(action)}
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    requestAction(action.key, job);
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          );
        },
      },
    ],
    [requestAction],
  );

  return (
    <DashboardLayout activePath="/catalog/mapping/jobs">
      <PageHeader
        title="Approval Pipeline Jobs"
        subtitle="Monitor taxonomy approval jobs, inspect ledger history, and recover blocked session queues without leaving mapping review."
        breadcrumbs={[
          { label: "Catalog", active: false },
          { label: "Mapping Review", active: false },
          { label: "Approval Jobs", active: true },
        ]}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/catalog/mapping">
                <ListTree className="h-4 w-4" />
                Back to Mapping Review
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => void refresh()}
              disabled={refreshing}
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </Button>
          </>
        }
      />

      {errorMessage ? (
        <Card className="mb-6 border-rose-200 dark:border-rose-900/70">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600 dark:text-rose-300" />
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Unable to fully load approval jobs
                </p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  {errorMessage}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="mb-6 grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-lg bg-indigo-50 p-3 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-300">
              <Clock3 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Active jobs
              </p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {summary.active}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-lg bg-rose-50 p-3 text-rose-600 dark:bg-rose-950 dark:text-rose-300">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Failed jobs
              </p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {summary.failed}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-lg bg-emerald-50 p-3 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-300">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Retryable
              </p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {summary.retryable}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-lg bg-slate-100 p-3 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              <Wrench className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Total listed
              </p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {summary.total}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardContent className="p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Approval job queue
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Select a row to inspect its ledger trail and use the recovery
                action that makes sense for that exact failure mode.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by job, session, owner, detail"
                className="sm:w-80"
              />
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              >
                <option value="ALL">All statuses</option>
                <option value="QUEUED">Queued</option>
                <option value="DISPATCHING">Dispatching</option>
                <option value="RUNNING">Running</option>
                <option value="FAILED">Failed</option>
                <option value="SUCCESS">Success</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div
        className={`mt-6 grid gap-6 ${
          sidebarCollapsed
            ? "xl:grid-cols-[minmax(0,1fr)_56px]"
            : "xl:grid-cols-[minmax(0,1fr)_420px]"
        }`}
      >
        <div className="min-w-0">
          <DataTable
            columns={columns}
            data={filteredJobs as unknown as Array<Record<string, unknown>>}
            loading={loading}
            sortable={false}
            onRowClick={(row) => {
              setSelectedSyncJobId(String(row.syncJobId ?? "") || null);
              setSidebarCollapsed(false);
            }}
            getRowClassName={(row) =>
              String(row.syncJobId ?? "") === selectedSyncJobId
                ? "bg-indigo-50/80 dark:bg-indigo-950/30 shadow-[inset_3px_0_0_rgb(79_70_229)] hover:bg-indigo-50 dark:hover:bg-indigo-950/40"
                : ""
            }
            emptyTitle="No approval jobs found"
            emptyDescription="No approval jobs match the current filters."
            emptyIcon={Clock3}
          />
        </div>

        <DetailsSidebar
          title={selectedJobDetail?.job.syncJobId ?? "Approval job"}
          eyebrow="Approval job"
          subtitle={
            selectedJobDetail
              ? `Session ${selectedJobDetail.job.mappingSessionId}`
              : null
          }
          status={
            selectedJobDetail ? (
              <StatusBadge
                status={mapJobStatusToBadge(selectedJobDetail.job.status)}
              >
                {selectedJobDetail.job.status.toLowerCase()}
              </StatusBadge>
            ) : null
          }
          actions={
            selectedJobDetail ? (
              <>
                {selectedJobDetail.job.actions.map((action) => (
                  <Button
                    key={action.key}
                    variant={formatActionVariant(action)}
                    size="sm"
                    onClick={() =>
                      requestAction(action.key, selectedJobDetail.job)
                    }
                  >
                    {action.key === "RETRY" ? (
                      <RotateCcw className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    {action.label}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    requestResumeQueue(selectedJobDetail.job.mappingSessionId)
                  }
                >
                  <RefreshCw className="h-4 w-4" />
                  Resume Queue
                </Button>
              </>
            ) : null
          }
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          emptyTitle="Select an approval job"
          emptyDescription="Click a row to inspect payload, ledger history, and recovery actions."
        >
          {selectedJobLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          ) : selectedJobDetail ? (
            <div className="space-y-5">
              <div className="flex flex-wrap gap-2">
                {selectedJobDetail.job.taxonomyStatus ? (
                  <span className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    Taxonomy {selectedJobDetail.job.taxonomyStatus}
                  </span>
                ) : null}
              </div>

              <div className="grid gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Mapping Session
                  </p>
                  <p className="mt-2 font-mono text-sm text-slate-900 dark:text-slate-100">
                    {selectedJobDetail.job.mappingSessionId}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Resource Lock
                  </p>
                  <p className="mt-2 font-mono text-sm text-slate-900 dark:text-slate-100">
                    {selectedJobDetail.job.resourceLockKey ?? "—"}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Decisions
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-300">
                    <p>Fields: {selectedJobDetail.job.fieldDecisionCount}</p>
                    <p>
                      Canonical values:{" "}
                      {selectedJobDetail.job.canonicalValueDecisionCount}
                    </p>
                    <p>
                      Marketplace values:{" "}
                      {
                        selectedJobDetail.job
                          .marketplaceSpecificValueDecisionCount
                      }
                    </p>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Ownership
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-300">
                    <p>
                      Approved by: {selectedJobDetail.job.approvedBy ?? "—"}
                    </p>
                    <p>Pipeline: {selectedJobDetail.job.pipelineId ?? "—"}</p>
                    <p>
                      Updated:{" "}
                      {formatTimestamp(selectedJobDetail.job.updatedAt)}
                    </p>
                  </div>
                </div>
              </div>

              {selectedJobDetail.job.message || selectedJobDetail.job.error ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Latest message
                  </p>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                    {selectedJobDetail.job.message ??
                      selectedJobDetail.job.error}
                  </p>
                  {selectedJobDetail.job.notes ? (
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Notes: {selectedJobDetail.job.notes}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <JsonPanel
                title="Request payload"
                payload={selectedJobDetail.job.payload}
                emptyLabel="No job payload was captured for this approval request."
              />

              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Ledger history
                </p>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  This event trail shows how the job moved and what recovery has
                  already been attempted.
                </p>
                <div className="mt-4 max-h-[32rem] space-y-3 overflow-auto pr-1">
                  {selectedJobDetail.ledger.length > 0 ? (
                    selectedJobDetail.ledger.map((entry) => (
                      <LedgerEntryCard key={entry.id} entry={entry} />
                    ))
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                      No ledger entries yet for this job.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </DetailsSidebar>
      </div>

      <ConfirmationModal
        open={actionModal.open}
        onOpenChange={(open) => {
          if (!open) actionModal.close();
        }}
        title={actionModal.title}
        description={actionModal.description}
        confirmLabel={actionModal.confirmLabel}
        cancelLabel="Cancel"
        variant={actionModal.variant}
        requireReason
        reason={actionModal.reason}
        reasonLabel="Audit note"
        reasonPlaceholder="Explain why this recovery action is being taken."
        onReasonChange={actionModal.setReason}
        onCancel={actionModal.close}
        onConfirm={() => {
          void actionModal.confirm();
        }}
      />
    </DashboardLayout>
  );
}
